import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight, Eye, EyeOff, Shield, Check } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const GOLD = '#C9A961';

const AcademyResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token. Request a new link.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/academy/auth/reset-password', { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/academy/login', { replace: true }), 2200);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reset failed.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12" data-testid="academy-reset-page">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-10">
          <Link to="/academy" className="inline-block mb-6">
            <img
              src="/images/scholar-athlete-logo-new.png"
              alt="Scholar Athlete Academy"
              className="mx-auto h-20 w-auto"
              style={{ filter: `drop-shadow(0 0 40px rgba(255,94,0,0.25))` }}
            />
          </Link>
          <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
            <Shield className="w-3 h-3" style={{ color: GOLD }} />
            <span className="text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>
              Set New Password
            </span>
          </div>
        </div>

        {done ? (
          <div className="border-2 border-orange-500/60 p-8 text-center" data-testid="academy-reset-success">
            <div className="w-12 h-12 mx-auto bg-orange-500 text-black flex items-center justify-center mb-5">
              <Check className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-black uppercase tracking-tight mb-2">Password Updated</h2>
            <p className="text-sm text-gray-400">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-black uppercase tracking-tight text-center mb-2">
              Choose a New Password
            </h1>
            <p className="text-xs text-gray-500 tracking-widest uppercase text-center mb-8">
              Min 8 characters
            </p>

            {!token && (
              <div className="mb-6 border-2 border-red-500/40 bg-red-500/10 p-4 text-xs text-red-300 text-center">
                Missing reset token. <Link to="/academy/forgot-password" className="underline">Request a new link.</Link>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="academy-reset-form">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-black border-2 border-gray-800 px-4 py-4 pr-12 text-white font-mono text-sm focus:border-orange-500 outline-none transition-colors"
                    placeholder="••••••••"
                    data-testid="academy-reset-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-orange-500"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500 mb-2">
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-black border-2 border-gray-800 px-4 py-4 text-white font-mono text-sm focus:border-orange-500 outline-none transition-colors"
                  placeholder="••••••••"
                  data-testid="academy-reset-confirm"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !token}
                className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-gray-800 disabled:text-gray-600 text-black py-5 font-black text-sm tracking-[0.2em] uppercase transition-colors flex items-center justify-center gap-2"
                data-testid="academy-reset-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Updating
                  </>
                ) : (
                  <>
                    Update Password <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <Link
                to="/academy/login"
                className="text-[11px] uppercase tracking-[0.25em] text-gray-500 hover:text-orange-500 font-bold"
              >
                ← Back to Sign In
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AcademyResetPassword;
